import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categoryData } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGuidesOpen, setIsGuidesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(event.target as Node)) {
        setIsGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const linkClass = isScrolled
    ? 'text-foreground-700 hover:text-primary-500'
    : 'text-background-50/90 hover:text-primary-400';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-4">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-background-50 text-sm"></i>
              </div>
              <span className={`text-lg font-heading font-semibold whitespace-nowrap ${isScrolled ? 'text-foreground-900' : 'text-background-50'}`}>
                Hipobuy Spread
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium whitespace-nowrap transition-colors ${linkClass}`}>
                Home
              </Link>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => { setIsDropdownOpen(!isDropdownOpen); setIsGuidesOpen(false); }}
                  className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors ${linkClass}`}
                >
                  Categories
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg shadow-lg border border-background-200 py-2 max-h-80 overflow-y-auto z-50">
                    {categoryData.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div ref={guidesRef} className="relative">
                <button
                  onClick={() => { setIsGuidesOpen(!isGuidesOpen); setIsDropdownOpen(false); }}
                  className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors ${linkClass}`}
                >
                  Guides
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isGuidesOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isGuidesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg shadow-lg border border-background-200 py-2 z-50">
                    <Link
                      to="/hipobuy-spreadsheet-guide"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setIsGuidesOpen(false)}
                    >
                      Complete Beginner Guide
                    </Link>
                    <Link
                      to="/hipobuy-spreadsheet-vs-kakobuy"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setIsGuidesOpen(false)}
                    >
                      Hipobuy vs Kakobuy
                    </Link>
                    <Link
                      to="/hipobuy-spreadsheet-safety"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setIsGuidesOpen(false)}
                    >
                      Safety Guide
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Explore Database
              <i className="ri-arrow-right-up-line"></i>
            </a>

            <button
              className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <i className={`ri-menu-line text-xl ${isScrolled ? 'text-foreground-900' : 'text-background-50'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile slide-out menu overlay */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 md:hidden ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>

        <div
          className={`absolute top-0 right-0 h-full w-72 bg-background-50 shadow-xl transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
            <Link to="/" className="flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
              <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-background-50 text-xs"></i>
              </div>
              <span className="text-base font-heading font-semibold text-foreground-900">Hipobuy Spread</span>
            </Link>
            <button
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <i className="ri-close-line text-foreground-700 text-lg"></i>
            </button>
          </div>

          <div className="overflow-y-auto h-[calc(100%-65px)] py-4">
            <div className="px-5 space-y-1 mb-4">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Home
              </Link>
            </div>

            <div className="px-5 mb-4">
              <p className="text-xs font-label uppercase tracking-wider text-foreground-400 mb-2 px-3">Guides</p>
              <div className="space-y-1">
                <Link
                  to="/hipobuy-spreadsheet-guide"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-book-open-line w-5 h-5 flex items-center justify-center"></i>
                  Beginner Guide
                </Link>
                <Link
                  to="/hipobuy-spreadsheet-vs-kakobuy"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-scales-line w-5 h-5 flex items-center justify-center"></i>
                  Hipobuy vs Kakobuy
                </Link>
                <Link
                  to="/hipobuy-spreadsheet-safety"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center"></i>
                  Safety Guide
                </Link>
              </div>
            </div>

            <div className="px-5 mb-4">
              <p className="text-xs font-label uppercase tracking-wider text-foreground-400 mb-2 px-3">Categories</p>
              <div className="space-y-0.5">
                {categoryData.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-between px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="whitespace-nowrap">{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                  </a>
                ))}
              </div>
            </div>

            <div className="px-5 pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Explore Database
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}